<template>
  <section class="mt-10">
    <div class="w-full">
      <div class="mb-6">
        <h1 class="text-3xl font-semibold text-gray-900">
          Edit Event
        </h1>
        <p class="text-sm text-gray-600 mt-1">
          Update event details for members.
        </p>
      </div>
      <div
        v-if="loading"
        class="text-sm text-gray-500"
      >
        Loading event...
      </div>
      <div
        v-else-if="error"
        class="text-sm text-red-600"
      >
        {{ error }}
      </div>
      <div
        v-else
        class="bg-white border border-gray-200 shadow-sm rounded-xl p-6"
      >
        <form @submit.prevent="updateEvent">
          <div class="grid gap-6 md:grid-cols-2">
            <div>
              <label class="block mb-2 text-sm font-medium text-gray-700">Title</label>
              <input
                v-model="form.title"
                class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                required
                type="text"
              >
            </div>
            <div>
              <label class="block mb-2 text-sm font-medium text-gray-700">Location</label>
              <input
                v-model="form.location"
                class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                type="text"
              >
            </div>
            <div>
              <label class="block mb-2 text-sm font-medium text-gray-700">Date</label>
              <input
                v-model="form.date"
                class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                required
                type="date"
              >
            </div>
            <div>
              <label class="block mb-2 text-sm font-medium text-gray-700">Type</label>
              <input
                v-model="form.type"
                class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                type="text"
              >
            </div>
            <div>
              <label class="block mb-2 text-sm font-medium text-gray-700">Price (GBP)</label>
              <input
                v-model.number="form.price"
                class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                min="0"
                step="0.01"
                type="number"
              >
            </div>
            <div>
              <label class="block mb-2 text-sm font-medium text-gray-700">Ticket URL</label>
              <input
                v-model="form.ticketUrl"
                class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                type="url"
              >
            </div>
            <div>
              <label class="block mb-2 text-sm font-medium text-gray-700">Banner Image Upload</label>
              <input
                accept="image/*"
                class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                type="file"
                @change="handleBannerUpload"
              >
              <div class="mt-2">
                <div
                  v-if="bannerUpload.status === 'uploading'"
                  class="text-xs text-gray-600"
                >
                  Uploading banner... {{ bannerUpload.progress }}%
                </div>
                <div
                  v-else-if="bannerUpload.status === 'success'"
                  class="text-xs text-green-700"
                >
                  Banner uploaded successfully.
                </div>
                <div
                  v-else-if="bannerUpload.status === 'error'"
                  class="text-xs text-red-600"
                >
                  {{ bannerUpload.error }}
                </div>
                <div
                  v-if="bannerUpload.status === 'uploading'"
                  class="mt-2 h-2 w-full rounded-full bg-gray-200"
                >
                  <div
                    class="h-2 rounded-full bg-black transition-all"
                    :style="{ width: `${bannerUpload.progress}%` }"
                  />
                </div>
                <div
                  v-if="bannerPreview.status === 'loading'"
                  class="mt-3 text-xs text-gray-600"
                >
                  Loading banner preview...
                </div>
                <div
                  v-else-if="bannerPreview.status === 'error'"
                  class="mt-3 text-xs text-red-600"
                >
                  {{ bannerPreview.error }}
                </div>
                <div
                  v-else-if="bannerPreview.status === 'ready'"
                  class="mt-3 space-y-2"
                >
                  <img
                    alt="Banner preview"
                    class="w-full rounded-lg border border-gray-200"
                    :src="bannerPreview.url"
                  >
                  <button
                    class="text-xs font-semibold text-red-600 hover:text-red-700"
                    type="button"
                    @click="removeBanner"
                  >
                    Remove banner
                  </button>
                </div>
              </div>
            </div>
            <div class="md:col-span-2">
              <label class="inline-flex items-center text-sm font-medium text-gray-700">
                <input
                  v-model="form.hidePrice"
                  class="mr-2 h-4 w-4 rounded border-gray-300"
                  type="checkbox"
                >
                Hide price (show as paid event)
              </label>
            </div>
            <div>
              <label class="block mb-2 text-sm font-medium text-gray-700">Start Time</label>
              <input
                v-model="form.startTime"
                class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                type="time"
              >
            </div>
            <div>
              <label class="block mb-2 text-sm font-medium text-gray-700">End Time</label>
              <input
                v-model="form.endTime"
                class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                type="time"
              >
            </div>
            <div class="md:col-span-2">
              <label class="block mb-2 text-sm font-medium text-gray-700">Who Should Join</label>
              <div class="grid gap-3 sm:grid-cols-2">
                <label
                  v-for="option in whoShouldJoinOptions"
                  :key="option"
                  class="flex items-center gap-2 text-sm text-gray-700"
                >
                  <input
                    v-model="form.whoShouldJoin"
                    class="h-4 w-4 rounded border-gray-300"
                    type="checkbox"
                    :value="option"
                  >
                  {{ option }}
                </label>
              </div>
            </div>
            <div class="md:col-span-2">
              <label class="block mb-2 text-sm font-medium text-gray-700">Description</label>
              <textarea
                v-model="form.description"
                class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                rows="3"
              />
            </div>
          </div>
          <div class="mt-6 flex items-center gap-3">
            <button
              class="inline-flex items-center rounded-lg bg-black px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800"
              :disabled="bannerUpload.status === 'uploading'"
              type="submit"
            >
              Save Changes
            </button>
            <router-link
              class="text-sm font-medium text-gray-600 hover:text-gray-900"
              to="/administrator/events"
            >
              Cancel
            </router-link>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import {defineComponent, onMounted, ref, watchEffect} from 'vue';
import axios from 'axios';
import {Event} from '@/types';
import {useHead} from "@vueuse/head";

export default defineComponent({
  name: 'EventEdit',
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const loading = ref(false);
    const error = ref('');
    const bannerUpload = ref({
      status: 'idle',
      progress: 0,
      error: null as string | null,
    });
    const bannerPreview = ref({
      status: 'idle',
      url: '',
      error: null as string | null,
    });
    const form = ref({
      title: '',
      location: '',
      date: '',
      startTime: '',
      endTime: '',
      type: '',
      price: undefined as number | undefined,
      hidePrice: false,
      ticketUrl: '',
      bannerImageUrl: '',
      whoShouldJoin: [] as string[],
      description: '',
    });

    const fetchEvent = async (): Promise<void> => {
      loading.value = true;
      try {
        const response = await axios.get<Event>(`/events/${props.id}`);
        const event = response.data;
        form.value = {
          title: event.title,
          location: event.location || '',
          date: event.date ? event.date.slice(0, 10) : '',
          startTime: event.startTime || '',
          endTime: event.endTime || '',
          type: event.type || '',
          price: event.price == null ? undefined : Number(event.price),
          hidePrice: Boolean(event.hidePrice),
          ticketUrl: event.ticketUrl || '',
          bannerImageUrl: event.bannerImageUrl || '',
          whoShouldJoin: event.whoShouldJoin || [],
          description: event.description || '',
        };
        await loadBannerPreview();
      } catch (err) {
        console.error(err);
        error.value = 'Failed to load event.';
      } finally {
        loading.value = false;
      }
    };

    const updateEvent = async (): Promise<void> => {
      error.value = '';
      try {
        await axios.put(`/events/${props.id}`, {
          ...form.value,
          price: form.value.price ?? undefined,
          ticketUrl: form.value.ticketUrl || undefined,
          bannerImageUrl: form.value.bannerImageUrl || null,
          whoShouldJoin: form.value.whoShouldJoin.length ? form.value.whoShouldJoin : undefined,
        });
      } catch (err) {
        console.error(err);
        error.value = 'Failed to update event.';
      }
    };

    const resetBannerPreview = () => {
      bannerPreview.value = {
        status: 'idle',
        url: '',
        error: null,
      };
    };

    const loadBannerPreview = async (): Promise<void> => {
      if (!form.value.bannerImageUrl) {
        resetBannerPreview();
        return;
      }

      const raw = form.value.bannerImageUrl.trim();
      if (raw.startsWith('http://') || raw.startsWith('https://')) {
        bannerPreview.value = {
          status: 'ready',
          url: raw,
          error: null,
        };
        return;
      }

      bannerPreview.value = {
        status: 'loading',
        url: '',
        error: null,
      };

      try {
        const response = await axios.get<{ url: string }>(
          `/video/get-signed-url?fileName=${encodeURIComponent(form.value.bannerImageUrl)}`
        );
        bannerPreview.value = {
          status: 'ready',
          url: response.data.url,
          error: null,
        };
      } catch (err) {
        console.error('Failed to load banner preview:', err);
        bannerPreview.value = {
          status: 'error',
          url: '',
          error: 'Failed to load banner preview.',
        };
      }
    };

    const handleBannerUpload = async (event: globalThis.Event): Promise<void> => {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) return;

      bannerUpload.value = {
        status: 'uploading',
        progress: 0,
        error: null,
      };

      const formData = new FormData();
      formData.append('image', file);

      try {
        const response = await axios.post<{ imageUrl: string }>('/upload/banner', formData, {
          onUploadProgress: (progressEvent) => {
            const total = progressEvent.total || 0;
            const percent = total ? Math.round((progressEvent.loaded / total) * 100) : 0;
            bannerUpload.value = {
              status: 'uploading',
              progress: percent,
              error: null,
            };
          },
        });

        form.value.bannerImageUrl = response.data.imageUrl;
        bannerUpload.value = {
          status: 'success',
          progress: 100,
          error: null,
        };
        await loadBannerPreview();
      } catch (err) {
        console.error('Failed to upload banner:', err);
        bannerUpload.value = {
          status: 'error',
          progress: 0,
          error: 'Banner upload failed. Please try again.',
        };
      }
    };

    const removeBanner = (): void => {
      form.value.bannerImageUrl = '';
      resetBannerPreview();
      bannerUpload.value = {
        status: 'idle',
        progress: 0,
        error: null,
      };
    };

    onMounted(fetchEvent);

    watchEffect(() => {
      useHead({
        title: 'Edit Event',
      });
    });

    return {
      loading,
      error,
      bannerUpload,
      bannerPreview,
      form,
      updateEvent,
      handleBannerUpload,
      removeBanner,
      whoShouldJoinOptions: [
        'VC Fund Managing Partners and Partners',
        'Crypto Fund Managing Partners and Partners',
        'Business Angels and Crypto Angel Investors',
        'Family Office Investment Team Members',
        'High Net Worth Individuals who are active LPs in Funds or Syndicates',
        'Sovereign Wealth Fund Managers',
        'Institutional Investors',
      ],
    };
  },
});
</script>
